import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Brutalist - a small primitive kit the rest of the Operations Command UI
 * composes against. The rules:
 *
 *   - Corners: 0px always. No exceptions.
 *   - Borders: 2px minimum, 3px on top-level surfaces.
 *   - Shadows: sharp #000000, 0 blur, positive XY offset ("3D-Offset").
 *   - Color: #E11900 is reserved for Manual Intervention / Emergency.
 *     #00FF41 is reserved for Autonomous Success. Everything else is
 *     ink, paper, or slate.
 *   - Fonts: Inter (headers, UI), IBM Plex Mono (data, logs, IDs, time).
 *
 * Every primitive here is inline-styled so it renders correctly even
 * before Tailwind is in scope (auth shell, public pages, etc.).
 */

/**
 * Mechanical press bus - any BrutalButton press increments `pressGen` so
 * diagnostic UI (actuator strip, heartbeat latch) can flash for 100ms
 * in lock-step with the 1px button translation.
 */
const BrutalPressContext = createContext({
  pressGen: 0,
  triggerMechanicalPress: () => {},
});

export function BrutalPressProvider({ children }) {
  const [pressGen, setPressGen] = useState(0);
  const triggerMechanicalPress = useCallback(() => {
    setPressGen((g) => g + 1);
  }, []);
  const value = useMemo(
    () => ({ pressGen, triggerMechanicalPress }),
    [pressGen, triggerMechanicalPress]);
  return (
    <BrutalPressContext.Provider value={value}>
      {children}
    </BrutalPressContext.Provider>
  );
}

export function useBrutalPress() {
  return useContext(BrutalPressContext);
}

export const BRUTAL = Object.freeze({
  ink: "#0A0A0A",
  inkSoft: "#171717",
  paper: "#FFFFFF",
  paperOff: "#FAFAFA",
  paperWarm: "#F7F7F5",
  rule: "#0A0A0A",
  mute: "#525252",
  subtle: "#A3A3A3",
  line: "#E5E5E5",
  success: "#00FF41",
  emergency: "#E11900",
  sansStack:
    "'Inter', 'Inter Tight', system-ui, ui-sans-serif, sans-serif",
  monoStack:
    "'IBM Plex Mono', ui-monospace, monospace",
  shadow: "4px 4px 0 0 #0A0A0A",
  shadowSm: "2px 2px 0 0 #0A0A0A",
  shadowLg: "8px 8px 0 0 #0A0A0A",
  shadowEmergency: "4px 4px 0 0 #E11900",
  shadowSuccess: "4px 4px 0 0 #00FF41",
});

export function BrutalHeading({ level = 1, eyebrow, children, style }) {
  const Tag = `h${level}`;
  const sizes = {
    1: "48px",
    2: "34px",
    3: "22px",
    4: "16px",
  };
  return (
    <div style={{ marginBottom: "8px" }}>
      {eyebrow ? (
        <div
          style={{
            fontFamily: BRUTAL.monoStack,
            fontSize: "11px",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: BRUTAL.subtle,
            marginBottom: "10px",
            fontWeight: 700,
          }}
        >
          {eyebrow}
        </div>
      ) : null}
      <Tag
        style={{
          fontFamily: BRUTAL.sansStack,
          fontSize: sizes[level] || sizes[1],
          fontWeight: 900,
          letterSpacing: "-0.02em",
          textTransform: "uppercase",
          lineHeight: 1,
          margin: 0,
          color: BRUTAL.ink,
          ...style,
        }}
      >
        {children}
      </Tag>
    </div>
  );
}

export function BrutalLabel({ children, style }) {
  return (
    <span
      style={{
        fontFamily: BRUTAL.monoStack,
        fontSize: "10px",
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        fontWeight: 700,
        color: BRUTAL.mute,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function BrutalButton({
  children,
  onClick,
  variant = "primary",
  type = "button",
  style,
  ...rest
}) {
  const { triggerMechanicalPress } = useBrutalPress();
  const palettes = {
    primary: { bg: BRUTAL.ink, fg: BRUTAL.paper, border: BRUTAL.ink },
    danger: { bg: BRUTAL.emergency, fg: BRUTAL.paper, border: BRUTAL.ink },
    success: { bg: BRUTAL.success, fg: BRUTAL.ink, border: BRUTAL.ink },
    ghost: { bg: BRUTAL.paper, fg: BRUTAL.ink, border: BRUTAL.ink },
  };
  const p = palettes[variant] || palettes.primary;
  return (
    <button
      type={type}
      onClick={onClick}
      style={{
        fontFamily: BRUTAL.sansStack,
        fontSize: "13px",
        fontWeight: 800,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        padding: "12px 18px",
        background: p.bg,
        color: p.fg,
        border: `3px solid ${p.border}`,
        boxShadow: BRUTAL.shadow,
        cursor: "pointer",
        borderRadius: 0,
        /* Mechanical: stepper timing, no ease curves. */
        transition:
          "transform 100ms steps(2, end), box-shadow 100ms steps(2, end)",
        ...style,
      }}
      onMouseDown={(e) => {
        triggerMechanicalPress();
        e.currentTarget.style.transform = "translate(1px, 1px)";
        e.currentTarget.style.boxShadow = BRUTAL.shadowSm;
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "translate(0, 0)";
        e.currentTarget.style.boxShadow = BRUTAL.shadow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translate(0, 0)";
        e.currentTarget.style.boxShadow = BRUTAL.shadow;
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function BrutalPanel({
  title,
  eyebrow,
  children,
  tone = "neutral",
  footer,
  style,
}) {
  const tones = {
    neutral: { border: BRUTAL.ink, bg: BRUTAL.paper, shadow: BRUTAL.shadow },
    emergency: {
      border: BRUTAL.ink,
      bg: BRUTAL.paper,
      shadow: BRUTAL.shadowEmergency,
    },
    success: {
      border: BRUTAL.ink,
      bg: BRUTAL.paper,
      shadow: BRUTAL.shadowSuccess,
    },
    ink: { border: BRUTAL.ink, bg: BRUTAL.ink, shadow: BRUTAL.shadow },
  };
  const t = tones[tone] || tones.neutral;
  const fg = tone === "ink" ? BRUTAL.paper : BRUTAL.ink;
  return (
    <section
      style={{
        border: `3px solid ${t.border}`,
        background: t.bg,
        color: fg,
        boxShadow: t.shadow,
        borderRadius: 0,
        ...style,
      }}
    >
      {title || eyebrow ? (
        <header
          style={{
            padding: "14px 16px",
            borderBottom: `2px solid ${t.border}`,
            background: tone === "ink" ? BRUTAL.ink : BRUTAL.paperOff,
            color: fg,
          }}
        >
          {eyebrow ? (
            <div
              style={{
                fontFamily: BRUTAL.monoStack,
                fontSize: "10px",
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: tone === "ink" ? BRUTAL.subtle : BRUTAL.mute,
                marginBottom: title ? "6px" : 0,
                fontWeight: 700,
              }}
            >
              {eyebrow}
            </div>
          ) : null}
          {title ? (
            <div
              style={{
                fontFamily: BRUTAL.sansStack,
                fontSize: "18px",
                fontWeight: 900,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {title}
            </div>
          ) : null}
        </header>
      ) : null}
      <div style={{ padding: "16px" }}>{children}</div>
      {footer ? (
        <footer
          style={{
            padding: "10px 16px",
            borderTop: `2px solid ${t.border}`,
            background: tone === "ink" ? BRUTAL.inkSoft : BRUTAL.paperOff,
            fontFamily: BRUTAL.monoStack,
            fontSize: "10px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: tone === "ink" ? BRUTAL.subtle : BRUTAL.mute,
          }}
        >
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

export function BrutalStat({ label, value, tone = "neutral" }) {
  const colorMap = {
    neutral: BRUTAL.ink,
    success: BRUTAL.success,
    emergency: BRUTAL.emergency,
  };
  return (
    <div
      style={{
        padding: "14px 16px",
        border: `3px solid ${BRUTAL.ink}`,
        background: BRUTAL.paper,
        boxShadow: BRUTAL.shadowSm,
      }}
    >
      <BrutalLabel>{label}</BrutalLabel>
      <div
        style={{
          marginTop: "6px",
          fontFamily: BRUTAL.sansStack,
          fontSize: "32px",
          fontWeight: 900,
          letterSpacing: "-0.02em",
          color: colorMap[tone] || BRUTAL.ink,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}
